import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { api, endpoints } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  DocumentTextIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  ClockIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { Plus, MoreVertical, Copy, Trash2, Download, Upload, FileSpreadsheet, CheckCircle2, XCircle, RefreshCw, Timer, AlertTriangle } from 'lucide-react';
// Dropdown menu components removed - using custom implementation
import { useToast } from '../../components/ui/use-toast';

const statusOptions = [
  { value: 'all', label: 'Tümü' },
  { value: 'initiated', label: 'Başlatıldı' },
  { value: 'in_progress', label: 'Devam Ediyor' },
  { value: 'completed', label: 'Tamamlandı' },
  { value: 'cancelled', label: 'İptal Edildi' },
];

// Removed priority options - will use billing institutions instead

const transportTypeOptions = [
  { value: 'all', label: 'Tümü' },
  { value: 'EVE_NAKIL', label: 'Eve Nakil' },
  { value: 'SAGLIK_TEDBIRI', label: 'Sağlık Tedbiri' },
  { value: 'EVE_NAKIL_EVDE_SAGLIK', label: 'Eve Nakil/Evde Sağlık' },
  { value: 'HASTANEYE_SEVK_EVDE_SAGLIK', label: 'Hastaneye Sevk/Evde Sağlık' },
  { value: 'KONSULTASYON', label: 'Konsültasyon' },
  { value: 'SERVIS_SEVKI', label: 'Servis Sevki' },
  { value: 'DIYALIZ_SEVK', label: 'Diyaliz Sevk' },
  { value: 'TIBBI_TETIK', label: 'Tıbbi Tetik' },
  { value: 'VAKA_GOREV_IPTAL', label: 'Vaka/Görev İptal' },
  { value: 'SOSYAL_ENDIKASYON', label: 'Sosyal Endikasyon' },
  { value: 'NICU_SEVK', label: 'NICU Sevk' },
  { value: 'KYB_SEVK', label: 'KYB Sevk' },
  { value: 'GYB_SEVK', label: 'GYB Sevk' },
  { value: 'NAKIL_RED', label: 'Nakil Red' },
  { value: 'PICU_SEVK', label: 'PICU Sevk' },
  { value: 'BAKIMEVI_SEVK', label: 'BAKIMEVİ Sevk' },
];

const CasesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [startDate, setStartDate] = useState(searchParams.get('startDate') || '');
  const [endDate, setEndDate] = useState(searchParams.get('endDate') || '');
  const [showFilters, setShowFilters] = useState(false);
  
  // Excel operations states
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const fileInputRef = useRef(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const scrollContainerRef = useRef(null);
  
  // Auto refresh states
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Dropdown dışında tıklanınca kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setActiveDropdown(null);
      }
    };
    
    if (activeDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeDropdown]);

  // Auto refresh effect - 3 saniyede bir yenile
  useEffect(() => {
    let intervalId;
    if (autoRefresh) {
      intervalId = setInterval(() => {
        queryClient.invalidateQueries(['cases']);
      }, 3000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [autoRefresh, queryClient]);

  // Manuel yenileme fonksiyonu
  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    await queryClient.invalidateQueries(['cases']);
    setTimeout(() => setIsRefreshing(false), 500);
  };

  // Get current filters from URL
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const currentStatus = searchParams.get('status') || 'all';
  const currentBillingInstitution = searchParams.get('billingInstitution') || 'all';
  const currentTransportType = searchParams.get('transportType') || 'all';
  const currentAssignedTeam = searchParams.get('assignedTeam') || 'all';
  const currentSearch = searchParams.get('search') || '';
  const currentStartDate = searchParams.get('startDate') || '';
  const currentEndDate = searchParams.get('endDate') || '';

  useEffect(() => {
    setStartDate(currentStartDate);
  }, [currentStartDate]);

  useEffect(() => {
    setEndDate(currentEndDate);
  }, [currentEndDate]);

  // Tablo ve export'ta ortak kullanılan filtre parametreleri
  const buildFilterParams = () => {
    const params = new URLSearchParams();

    // Arama yokken sadece normal vakaları göster (planlı olmayan)
    if (!currentSearch) {
      params.set('is_planned', 'false');
    }

    if (currentStatus !== 'all') params.set('status', currentStatus);
    if (currentBillingInstitution !== 'all') params.set('billing_institution', currentBillingInstitution);
    if (currentTransportType !== 'all') params.set('transportType', currentTransportType);
    if (currentAssignedTeam !== 'all') {
      params.set('assigned_team_id', currentAssignedTeam === 'unassigned' ? '0' : currentAssignedTeam);
    }
    if (currentSearch) params.set('search', currentSearch);

    // external_presidency / external_hospital için belirtilmemişse yılbaşından itibaren göster
    const yearStart = `${new Date().getFullYear()}-01-01`;
    if (currentStartDate) {
      params.set('date_from', currentStartDate);
    } else if (user?.role === 'external_presidency' || user?.role === 'external_hospital') {
      params.set('date_from', yearStart);
    }
    if (currentEndDate) params.set('date_to', currentEndDate);

    // external_hospital için hospital_id'ye göre filtrele
    if (user?.role === 'external_hospital' && user?.hospital_id) {
      params.set('hospital_id', user.hospital_id.toString());
    }

    return params;
  };

  // Build query parameters (sayfalama + sıralama ekler)
  const buildQueryParams = () => {
    const params = buildFilterParams();
    params.set('page', currentPage.toString());
    params.set('limit', '200');
    params.set('sort', 'createdAt:desc');
    return params.toString();
  };

  // Fetch cases
  const { data: casesData, isLoading, error } = useQuery({
    queryKey: ['cases', currentPage, currentStatus, currentBillingInstitution, currentTransportType, currentAssignedTeam, currentSearch, currentStartDate, currentEndDate],
    queryFn: () => api.get(`/cases?${buildQueryParams()}`).then(res => res.data),
  });

  // Scroll pozisyonunu sakla ve restore et
  useEffect(() => {
    const scrollKey = `cases-scroll-${window.location.pathname}-${currentPage}-${currentStatus}-${currentBillingInstitution}-${currentTransportType}-${currentAssignedTeam}-${currentSearch}`;
    
    // Ana scroll container'ı bul (Layout'taki main element)
    const findScrollContainer = () => {
      // Önce main element'i bul
      const mainElement = document.querySelector('main');
      if (mainElement && mainElement.scrollHeight > mainElement.clientHeight) {
        return mainElement;
      }
      // Eğer main scroll yapmıyorsa window kullan
      return window;
    };

    const scrollContainer = findScrollContainer();
    
    // Sayfa yüklendiğinde scroll pozisyonunu restore et
    const savedScrollPosition = sessionStorage.getItem(scrollKey);
    if (savedScrollPosition && !isLoading && casesData?.data?.cases) {
      setTimeout(() => {
        const scrollPosition = parseInt(savedScrollPosition, 10);
        if (scrollContainer === window) {
          window.scrollTo(0, scrollPosition);
        } else {
          scrollContainer.scrollTop = scrollPosition;
        }
      }, 200);
    }

    // Scroll olayını dinle ve pozisyonu sakla
    const handleScroll = () => {
      const scrollPosition = scrollContainer === window
        ? window.pageYOffset || document.documentElement.scrollTop
        : scrollContainer.scrollTop;
      sessionStorage.setItem(scrollKey, scrollPosition.toString());
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [currentPage, currentStatus, currentBillingInstitution, currentTransportType, currentAssignedTeam, currentSearch, isLoading, casesData]);

  // Fetch institutions for billing filter
  const { data: institutionsData } = useQuery({
    queryKey: ['institutions'],
    queryFn: () => api.get('/institutions').then(res => res.data.data),
  });

  // Fetch teams for assigned team filter
  const { data: teamsData } = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.get('/teams').then(res => res.data.data || []),
  });

  // External institution users should only see their own institution in the filter
  // external_presidency users should only see presidency institutions
  // external_hospital users should only see their hospital's başkanlık
  // external_parent_institution users should only see their child institutions
  const filteredInstitutions = React.useMemo(() => {
    if (!institutionsData) return [];
    if (user?.role === 'external_institution' && user?.institution_id) {
      return institutionsData.filter((inst) => inst.id === user.institution_id);
    }
    if (user?.role === 'external_presidency') {
      return institutionsData.filter((inst) => (inst.name || '').toUpperCase().includes('BAŞKANLIK'));
    }
    if (user?.role === 'external_hospital' && user?.hospital_institution_id) {
      return institutionsData.filter((inst) => inst.id === user.hospital_institution_id);
    }
    if (user?.role === 'external_parent_institution' && user?.institution_id) {
      return institutionsData.filter((inst) => inst.parent_institution_id === user.institution_id);
    }
    return institutionsData;
  }, [institutionsData, user]);

  const updateSearchParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === 'all') {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    // Reset to page 1 when filtering
    if (updates.page === undefined) {
      newParams.set('page', '1');
    }
    setSearchParams(newParams);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    updateSearchParams({ search: searchTerm });
  };

  const handleFilterChange = (filterType, value) => {
    updateSearchParams({ [filterType]: value });
  };

  const handleDateFilterApply = (event) => {
    event.preventDefault();
    updateSearchParams({
      startDate: startDate || '',
      endDate: endDate || ''
    });
  };

  const handleClearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    updateSearchParams({
      startDate: '',
      endDate: ''
    });
  };

  // Sütun başlığı tıklama handler'ları (toggle mantığı)
  const handleStatusColumnClick = () => {
    // Eğer zaten 'in_progress' ise 'all' yap, değilse 'in_progress' yap
    const newStatus = currentStatus === 'in_progress' ? 'all' : 'in_progress';
    updateSearchParams({ status: newStatus });
  };

  const handleBillingInstitutionColumnClick = () => {
    // Eğer zaten 'EMT Ambulans' ise 'all' yap, değilse 'EMT Ambulans' yap
    const newBillingInstitution = currentBillingInstitution === 'EMT Ambulans' ? 'all' : 'EMT Ambulans';
    updateSearchParams({ billingInstitution: newBillingInstitution });
  };

  // Excel Operations
  const handleTemplateDownload = async () => {
    try {
      setIsDownloadingTemplate(true);
      const response = await api.get(endpoints.excel.template, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'vaka-sablonu.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Başarılı",
        description: "Excel şablonu indirildi",
        variant: "success"
      });
    } catch (error) {
      console.error('Template download error:', error);
      toast({
        title: "Hata",
        description: "Excel şablonu indirilemedi",
        variant: "destructive"
      });
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleFileImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Reset file input
    event.target.value = '';

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast({
        title: "Hata",
        description: "Sadece Excel dosyaları (.xlsx, .xls) kabul edilir",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsImporting(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(endpoints.excel.import, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const { fileName, uploadDate, data: result } = response.data;
      toast({
        title: "Excel Import Başarılı",
        description: `📄 Dosya: ${fileName}\n📅 Tarih: ${uploadDate}\n✅ ${result.success} başarılı, ❌ ${result.failed} başarısız`,
        variant: result.failed === 0 ? "success" : "default",
        duration: 8000
      });
      
      // Hata varsa detayları göster
      if (result.failed > 0) {
        console.log('Import hataları:', result.errors);
      }

      // Refresh cases list
      queryClient.invalidateQueries(['cases']);

      // Show detailed results if there are errors
      if (result.failed > 0) {
        console.log('Import errors:', result.errors);
        // Could show a detailed error modal here
      }

    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: "Hata",
        description: error.response?.data?.message || "Excel dosyası içe aktarılamadı",
        variant: "destructive"
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);

      // Tablodaki filtrelerin aynısını kullan
      const params = buildFilterParams();

      const response = await api.get(`${endpoints.excel.export}?${params.toString()}`, {
        responseType: 'blob',
        timeout: 180000 // 3 dakika - büyük veri setleri için
      });
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const currentDate = new Date().toISOString().split('T')[0];
      link.download = `vakalar-${currentDate}.xlsx`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Başarılı",
        description: "Vakalar Excel dosyası olarak indirildi",
        variant: "success"
      });
    } catch (error) {
      console.error('Export error:', error);
      // responseType: 'blob' olduğu için JSON hata gövdesi Blob olarak gelir; metne çevir
      let message = '';
      const data = error.response?.data;
      if (data instanceof Blob) {
        try { message = JSON.parse(await data.text())?.message || ''; } catch { /* yok say */ }
      } else if (data?.message) {
        message = data.message;
      }
      if (!message && error.code === 'ECONNABORTED') {
        message = 'Dışa aktarma zaman aşımına uğradı. Lütfen daha dar bir tarih aralığı seçin.';
      }
      toast({
        title: "Hata",
        description: message || "Excel dışa aktarımı başarısız",
        variant: "destructive"
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Vaka kopyalama mutation (normal kopya)
  const copyMutation = useMutation({
    mutationFn: async (caseId) => {
      const caseDetail = await api.get(`/cases/${caseId}`).then(res => res.data);
      const originalCase = caseDetail.data.case;
      
      const copyData = {
        patient_name: originalCase.patient_name,
        patient_surname: originalCase.patient_surname,
        patient_birth_year: originalCase.patient_birth_year,
        patient_age: originalCase.patient_age,
        patient_address: originalCase.patient_address,
        patient_address_details: originalCase.patient_address_details,
        patient_nationality_type: originalCase.patient_nationality_type,
        patient_id_number: originalCase.patient_id_number,
        initial_description: `[KOPYA] ${originalCase.initial_description || ''}`,
        requesting_authority: originalCase.requesting_authority,
        call_giver_authority: originalCase.call_giver_authority,
        case_giver_type: originalCase.case_giver_type,
        case_giver_person: originalCase.case_giver_person,
        case_giver_institution: originalCase.case_giver_institution,
        case_giver_hospital: originalCase.case_giver_hospital,
        case_giver_presidency_id: originalCase.case_giver_presidency_id,
        transport_type: originalCase.transport_type,
        destination_address: originalCase.destination_address,
        destination_address_details: originalCase.destination_address_details,
        source_hospital_id: originalCase.source_hospital_id,
        destination_hospital_id: originalCase.destination_hospital_id,
        billing_institution: originalCase.billing_institution,
        protocol_number: '',
        request_date: new Date().toISOString().split('T')[0],
        request_time: new Date().toTimeString().substring(0, 5),
        is_planned: originalCase.is_planned || false,
      };
      
      return api.post('/cases/stage1', copyData);
    },
    onSuccess: () => {
      toast({
        title: "Başarılı",
        description: "Vaka başarıyla kopyalandı",
        variant: "success"
      });
      queryClient.invalidateQueries(['cases']);
      setActiveDropdown(null);
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: "Vaka kopyalanırken hata oluştu",
        variant: "destructive"
      });
      setActiveDropdown(null);
    }
  });

  // Vaka silme mutation
  const deleteMutation = useMutation({
    mutationFn: (caseId) => api.delete(`/cases/${caseId}`),
    onSuccess: () => {
      toast({
        title: "Başarılı",
        description: "Vaka başarıyla silindi",
        variant: "success"
      });
      queryClient.invalidateQueries(['cases']);
      setActiveDropdown(null);
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: "Vaka silinirken hata oluştu",
        variant: "destructive"
      });
      setActiveDropdown(null);
    }
  });

  // Gidiş-dönüş kopyalama mutation (adresleri ve hastaneleri ters çevirir)
  const copyReturnMutation = useMutation({
    mutationFn: async (caseId) => {
      const caseDetail = await api.get(`/cases/${caseId}`).then(res => res.data);
      const originalCase = caseDetail.data.case;
      
      const copyData = {
        patient_name: originalCase.patient_name,
        patient_surname: originalCase.patient_surname,
        patient_birth_year: originalCase.patient_birth_year,
        patient_age: originalCase.patient_age,
        // Hasta adresi = orijinal destinasyon adresi (ters çevir)
        patient_address: originalCase.destination_address,
        patient_address_details: originalCase.destination_address_details,
        patient_nationality_type: originalCase.patient_nationality_type,
        patient_id_number: originalCase.patient_id_number,
        initial_description: `[DÖNÜŞ KOPYA] ${originalCase.initial_description || ''}`,
        requesting_authority: originalCase.requesting_authority,
        call_giver_authority: originalCase.call_giver_authority,
        case_giver_type: originalCase.case_giver_type,
        case_giver_person: originalCase.case_giver_person,
        case_giver_institution: originalCase.case_giver_institution,
        case_giver_hospital: originalCase.case_giver_hospital,
        case_giver_presidency_id: originalCase.case_giver_presidency_id,
        transport_type: originalCase.transport_type,
        // Destinasyon = orijinal hasta adresi (ters çevir)
        destination_address: originalCase.patient_address,
        destination_address_details: originalCase.patient_address_details,
        // Hastaneleri ters çevir
        source_hospital_id: originalCase.destination_hospital_id,
        destination_hospital_id: originalCase.source_hospital_id,
        billing_institution: originalCase.billing_institution,
        protocol_number: '',
        request_date: new Date().toISOString().split('T')[0],
        request_time: new Date().toTimeString().substring(0, 5),
        is_planned: originalCase.is_planned || false,
      };
      
      return api.post('/cases/stage1', copyData);
    },
    onSuccess: () => {
      toast({ title: "Başarılı", description: "Gidiş-dönüş vakası oluşturuldu", variant: "success" });
      queryClient.invalidateQueries(['cases']);
      setActiveDropdown(null);
    },
    onError: () => {
      toast({ title: "Hata", description: "Gidiş-dönüş vaka oluşturulurken hata oluştu", variant: "destructive" });
      setActiveDropdown(null);
    }
  });

  const handleCopyCase = (caseItem) => {
    if (window.confirm(`"${caseItem.patient_name} ${caseItem.patient_surname}" vakasını kopyalamak istediğinizden emin misiniz?`)) {
      copyMutation.mutate(caseItem.id);
    }
    setActiveDropdown(null);
  };

  const handleCopyReturnCase = (caseItem) => {
    if (window.confirm(`"${caseItem.patient_name} ${caseItem.patient_surname}" vakası için gidiş-dönüş kopyası oluşturulsun mu? Adresler ve hastaneler ters çevrilecek.`)) {
      copyReturnMutation.mutate(caseItem.id);
    }
    setActiveDropdown(null);
  };

  const handleDeleteCase = (caseItem) => {
    if (window.confirm(`"${caseItem.patient_name} ${caseItem.patient_surname}" vakasını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`)) {
      deleteMutation.mutate(caseItem.id);
    }
    setActiveDropdown(null);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'initiated': { label: 'Başlatıldı', color: 'bg-yellow-100 text-yellow-800' },
      'in_progress': { label: 'Devam Ediyor', color: 'bg-orange-100 text-orange-800' },
      'completed': { label: 'Tamamlandı', color: 'bg-green-100 text-green-800' },
      'cancelled': { label: 'İptal Edildi', color: 'bg-red-100 text-red-800' },
    };

    const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      'low': { label: 'Düşük', color: 'bg-blue-100 text-blue-800' },
      'medium': { label: 'Orta', color: 'bg-yellow-100 text-yellow-800' },
      'high': { label: 'Yüksek', color: 'bg-orange-100 text-orange-800' },
      'critical': { label: 'Kritik', color: 'bg-red-100 text-red-800' },
    };

    const config = priorityConfig[priority] || { label: priority, color: 'bg-gray-100 text-gray-800' };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getDocumentStatus = (caseItem) => {
    const hasDocument = caseItem.document_path && caseItem.document_filename;
    const hasAsosDocument = caseItem.asos_document_path && caseItem.asos_document_filename;
    
    if (hasDocument && hasAsosDocument) {
      // Her iki belge de var
      return {
        status: 'complete',
        icon: <CheckCircle2 className="h-5 w-5 text-green-600" />,
        text: null
      };
    } else if (!hasDocument && !hasAsosDocument) {
      // Hiç belge yok
      return {
        status: 'none',
        icon: <XCircle className="h-5 w-5 text-gray-400" />,
        text: null
      };
    } else {
      // Bir belge eksik
      const missingDoc = !hasDocument ? 'Belge' : 'ASOS Belgesi';
      return {
        status: 'partial',
        icon: <CheckCircle2 className="h-5 w-5 text-yellow-600" />,
        text: `${missingDoc} Eksik`
      };
    }
  };

  // 2 saat deadline durumu hesaplama
  const getDeadlineStatus = (caseItem) => {
    // Sadece tamamlanmamış vakalar için göster
    if (!caseItem || caseItem.status === 'completed' || caseItem.status === 'cancelled') {
      return null;
    }

    const requestDate = caseItem.request_date;
    const requestTime = caseItem.request_time;

    if (!requestDate || !requestTime) return null;

    // Çağrı zamanını oluştur
    const [hours, minutes] = requestTime.split(':').map(Number);
    const callDateTime = new Date(requestDate);
    callDateTime.setHours(hours, minutes, 0, 0);

    // 2 saat ekle (deadline)
    const deadlineDateTime = new Date(callDateTime.getTime() + 2 * 60 * 60 * 1000);
    
    // Şu anki zaman
    const now = new Date();
    
    // Kalan süre (milisaniye)
    const remainingMs = deadlineDateTime.getTime() - now.getTime();
    const remainingMinutes = Math.floor(remainingMs / (1000 * 60));

    if (remainingMs <= 0) {
      // Süre geçmiş
      const overdueMinutes = Math.abs(remainingMinutes);
      const overdueHours = Math.floor(overdueMinutes / 60);
      const overdueRestMinutes = overdueMinutes % 60;
      return {
        status: 'expired',
        icon: <AlertTriangle className="h-4 w-4 text-red-600 animate-pulse" />,
        tooltip: overdueHours > 0 
          ? `Süre ${overdueHours}s ${overdueRestMinutes}dk geçti!`
          : `Süre ${overdueRestMinutes}dk geçti!`,
        bgClass: 'bg-red-100'
      };
    } else if (remainingMinutes <= 30) {
      // Son 30 dakika
      return {
        status: 'danger',
        icon: <Timer className="h-4 w-4 text-red-600" />,
        tooltip: `${remainingMinutes}dk kaldı!`,
        bgClass: 'bg-red-50'
      };
    } else if (remainingMinutes <= 60) {
      // Son 1 saat
      return {
        status: 'warning',
        icon: <Timer className="h-4 w-4 text-yellow-600" />,
        tooltip: `${remainingMinutes}dk kaldı`,
        bgClass: 'bg-yellow-50'
      };
    }
    
    // Normal süre - ikon gösterme
    return null;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('tr-TR', {
      timeZone: 'Europe/Istanbul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };



  const totalPages = Math.ceil((casesData?.data?.pagination?.total_items || 0) / 200);

  return (
    <div className="w-full px-4 py-8">
      {/* Auto Refresh Controls - Sağ üst köşe */}
      <div className="flex justify-end mb-2">
        <div className="flex items-center gap-2 bg-slate-800 rounded-lg px-3 py-1.5 shadow-sm">
          {/* Otomatik Toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-4 h-4 rounded border-gray-600 bg-slate-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-slate-800"
            />
            <span className="text-sm text-slate-200 select-none">Otomatik</span>
          </label>
          {/* Manuel Yenile Butonu */}
          <button
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            className="p-1.5 rounded-md hover:bg-slate-700 transition-colors disabled:opacity-50"
            title="Yenile"
          >
            <RefreshCw className={`h-4 w-4 text-slate-300 ${isRefreshing || (autoRefresh && isLoading) ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Vakalar</h1>
          <p className="text-muted-foreground">
            Acil tıp vakalarını görüntüleyin ve yönetin
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Excel Şablonu ve İçe Aktar - sadece tam yetkili kullanıcılar (paramedik, dış kurum, 112 başkanlık, 112 hastane dış hariç) */}
          {user?.role !== 'external_institution' && user?.role !== 'external_presidency' && user?.role !== 'paramedic' && user?.role !== 'external_hospital' && (
            <>
              <Button 
                variant="outline" 
                onClick={handleTemplateDownload}
                disabled={isDownloadingTemplate}
                className="text-xs sm:text-sm"
              >
                <FileSpreadsheet className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{isDownloadingTemplate ? 'İndiriliyor...' : 'Excel Şablonu'}</span>
              </Button>

              <div className="relative">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileImport}
                  className="hidden"
                />
                <Button 
                  variant="outline" 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isImporting}
                  className="text-xs sm:text-sm"
                >
                  <Upload className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">{isImporting ? 'İçe Aktarılıyor...' : 'Excel İçe Aktar'}</span>
                </Button>
              </div>
            </>
          )}

          {/* Excel Dışa Aktar - tüm roller görebilir */}
          <Button 
            variant="outline" 
            onClick={handleExport}
            disabled={isExporting}
            className="text-xs sm:text-sm"
          >
            <Download className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">{isExporting ? 'Dışa Aktarılıyor...' : 'Excel Dışa Aktar'}</span>
          </Button>

          {user?.role !== 'paramedic' && user?.role !== 'external_institution' && user?.role !== 'external_presidency' && (
            <Button onClick={() => navigate('/cases/new')}>
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Yeni Vaka</span>
            </Button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col space-y-4">
            {/* Search Bar */}
            <div className="flex flex-col md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-4">
              <div className="flex-1">
                <form onSubmit={handleSearch} className="flex space-x-2">
                  <Input
                    placeholder="ASOS protokol, hasta adı, soyadı ile arama..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-1"
                  />
                  <Button type="submit" variant="outline">
                    <MagnifyingGlassIcon className="h-4 w-4" />
                  </Button>
                </form>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2"
              >
                <FunnelIcon className="h-4 w-4" />
                <span>Filtreler</span>
              </Button>
            </div>

            {/* Filters */}
            {showFilters && (
              <>
                <div className="border-t pt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Durum</label>
                  <select
                    value={currentStatus}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Faturalanan Kurum</label>
                  <select
                    value={currentBillingInstitution}
                    onChange={(e) => handleFilterChange('billingInstitution', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="all">Tümü</option>
                    {filteredInstitutions?.map((institution) => (
                      <option key={institution.id} value={institution.name}>
                        {institution.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Nakil Türü</label>
                  <select
                    value={currentTransportType}
                    onChange={(e) => handleFilterChange('transportType', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                  >
                    {transportTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Atanan Ekip</label>
                  <select
                    value={currentAssignedTeam}
                    onChange={(e) => handleFilterChange('assignedTeam', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="all">Tümü</option>
                    <option value="unassigned">Atanmamış</option>
                    {teamsData?.map((team) => (
                      <option key={team.id} value={team.id.toString()}>
                        {team.name} {team.code ? `(${team.code})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
                <div className="border-t pt-4">
                  <form onSubmit={handleDateFilterApply} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Başlangıç Tarihi</label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Bitiş Tarihi</label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div className="flex items-end space-x-2">
                      <Button type="submit" className="flex-1">
                        Tarihleri Uygula
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        disabled={!startDate && !endDate}
                        onClick={handleClearDateFilter}
                      >
                        Temizle
                      </Button>
                    </div>
                  </form>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Vaka Listesi</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <ClockIcon className="h-8 w-8 mx-auto mb-2 text-muted-foreground animate-spin" />
                <p className="text-muted-foreground">Vakalar yükleniyor...</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600">Hata: {error.message}</p>
              <Button variant="outline" onClick={() => window.location.reload()} className="mt-2">
                Yeniden Dene
              </Button>
            </div>
          ) : casesData?.data?.cases?.length === 0 ? (
            <div className="text-center py-8">
              <DocumentTextIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">Henüz vaka bulunmuyor</h3>
              <p className="text-muted-foreground mb-4">
                İlk vakayı oluşturmak için "Yeni Vaka" butonuna tıklayın.
              </p>
              {user?.role !== 'paramedic' && user?.role !== 'external_institution' && user?.role !== 'external_presidency' && (
                <Button onClick={() => navigate('/cases/new')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Yeni Vaka
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Cases Table */}
              <div className="overflow-x-auto" ref={scrollContainerRef}>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium">Vaka No</th>
                      <th className="text-left py-3 px-4 font-medium">Hasta</th>
                      <th className="text-left py-3 px-4 font-medium">ASOS Protokol</th>
                      <th className="text-left py-3 px-4 font-medium">Belge Durumu</th>
                      <th 
                        className={`text-left py-3 px-4 font-medium cursor-pointer hover:bg-gray-100 transition-colors select-none ${
                          currentStatus === 'in_progress' ? 'bg-orange-50 text-orange-700' : ''
                        }`}
                        onClick={handleStatusColumnClick}
                        title={currentStatus === 'in_progress' ? 'Tüm vakaları göster (filtreyi kaldır)' : 'Devam eden vakaları göster'}
                      >
                        Durum
                        {currentStatus === 'in_progress' && (
                          <span className="ml-1 text-xs">✓</span>
                        )}
                      </th>
                      <th className="text-left py-3 px-4 font-medium">Atanan Ekip</th>
                      <th className="text-left py-3 px-4 font-medium">Hasta Adresi</th>
                      <th className="text-left py-3 px-4 font-medium">Gideceği Adres</th>
                      <th className="text-left py-3 px-4 font-medium">Tarih</th>
                      <th 
                        className={`text-left py-3 px-4 font-medium cursor-pointer hover:bg-gray-100 transition-colors select-none ${
                          currentBillingInstitution === 'EMT Ambulans' ? 'bg-blue-50 text-blue-700' : ''
                        }`}
                        onClick={handleBillingInstitutionColumnClick}
                        title={currentBillingInstitution === 'EMT Ambulans' ? 'Tüm kurumları göster (filtreyi kaldır)' : 'EMT Ambulans vakalarını göster'}
                      >
                        Faturalanan Kurum
                        {currentBillingInstitution === 'EMT Ambulans' && (
                          <span className="ml-1 text-xs">✓</span>
                        )}
                      </th>
                      <th className="text-left py-3 px-4 font-medium">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {casesData?.data?.cases?.map((caseItem) => {
                      const isMobile = caseItem.case_type === 'mobile';
                      const deadlineStatus = getDeadlineStatus(caseItem);
                      const isPresidency = caseItem.case_giver_type === 'presidency';
                      const rowClass = caseItem.is_planned && isPresidency
                        ? 'border-teal-400 bg-gradient-to-r from-orange-50 to-teal-50 hover:from-orange-100 hover:to-teal-100'
                        : caseItem.is_planned
                        ? 'border-orange-300 bg-orange-50 hover:bg-orange-100'
                        : isPresidency
                        ? 'border-teal-300 bg-teal-50 hover:bg-teal-100'
                        : isMobile
                        ? 'border-blue-300 bg-blue-50 hover:bg-blue-100'
                        : deadlineStatus?.status === 'expired'
                        ? 'border-red-300 bg-red-50 hover:bg-red-100'
                        : deadlineStatus?.status === 'danger'
                        ? 'border-red-200 bg-red-25 hover:bg-red-50'
                        : '';
                      
                      return (
                      <tr key={caseItem.id} className={`border-b hover:bg-muted/50 ${rowClass}`}>
                        <td className="py-3 px-4">
                          <div className="flex flex-col">
                            <div className="flex items-center space-x-1">
                              <Link 
                                to={`/cases/${caseItem.id}`}
                                className="font-mono text-sm text-blue-600 hover:text-blue-800"
                              >
                                #{caseItem.case_number}
                              </Link>
                              {/* Deadline Uyarı İkonu */}
                              {deadlineStatus && (
                                <span 
                                  className="inline-flex items-center" 
                                  title={deadlineStatus.tooltip}
                                >
                                  {deadlineStatus.icon}
                                </span>
                              )}
                            </div>
                            {(caseItem.is_planned || caseItem.case_giver_type === 'presidency' || isMobile) && (
                              <div className="flex items-center flex-wrap gap-1 mt-1">
                                {caseItem.is_planned && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-orange-200 text-orange-800">
                                    Planlı
                                  </span>
                                )}
                                {caseItem.case_giver_type === 'presidency' && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#b2f0ec', color: '#0d7a73' }}>
                                    112 Vakası
                                  </span>
                                )}
                                {isMobile && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-200 text-blue-800">
                                    Mobil
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <div className="font-medium">
                              {caseItem.patient_name} {caseItem.patient_surname}
                            </div>
                            {caseItem.patient_age && (
                              <div className="text-sm text-muted-foreground">
                                {caseItem.patient_age} yaş
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm">
                            {caseItem.protocol_number || '-'}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {(() => {
                            const docStatus = getDocumentStatus(caseItem);
                            return (
                              <div className="flex items-center space-x-2">
                                <div className="border border-gray-300 rounded p-1.5 flex items-center justify-center">
                                  {docStatus.icon}
                                </div>
                                {docStatus.text && (
                                  <span className="text-xs text-yellow-700 font-medium">
                                    {docStatus.text}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="py-3 px-4">
                          {getStatusBadge(caseItem.status)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm">
                            {caseItem.team_name || 'Atanmamış'}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm font-medium">
                            {caseItem.patient_address || '-'}
                          </div>
                          {caseItem.patient_address_details && (
                            <div className="text-xs text-muted-foreground mt-1">
                              {caseItem.patient_address_details}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm font-medium">
                            {caseItem.destination_address || '-'}
                          </div>
                          {caseItem.destination_address_details && (
                            <div className="text-xs text-muted-foreground mt-1">
                              {caseItem.destination_address_details}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm">
                            {new Date(caseItem.request_date).toLocaleDateString('tr-TR', {
                              timeZone: 'Europe/Istanbul'
                            })} {caseItem.request_time}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm">
                            {caseItem.billing_institution || 'Atanmamış'}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                // Scroll pozisyonunu sakla
                                const scrollKey = `cases-scroll-${window.location.pathname}-${currentPage}-${currentStatus}-${currentBillingInstitution}-${currentTransportType}-${currentSearch}`;
                                const mainElement = document.querySelector('main');
                                const scrollContainer = mainElement && mainElement.scrollHeight > mainElement.clientHeight 
                                  ? mainElement 
                                  : window;
                                const scrollPosition = scrollContainer === window
                                  ? window.pageYOffset || document.documentElement.scrollTop
                                  : scrollContainer.scrollTop;
                                sessionStorage.setItem(scrollKey, scrollPosition.toString());
                                navigate(`/cases/${caseItem.id}`);
                              }}
                            >
                              <EyeIcon className="h-4 w-4 mr-1" />
                              Detay Gör
                            </Button>
                            
                            {/* Üç nokta menüsü - 112 başkanlık kullanıcıları için gizli */}
                            {user?.role !== 'external_presidency' && (
                            <div className="relative">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setActiveDropdown(activeDropdown === caseItem.id ? null : caseItem.id)}
                                className="p-2"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                              
                              {activeDropdown === caseItem.id && (
                                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[120px]">
                                  <button
                                    onClick={() => handleCopyCase(caseItem)}
                                    disabled={copyMutation.isLoading}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2 first:rounded-t-lg"
                                  >
                                    <Copy className="h-4 w-4" />
                                    <span>Kopyala</span>
                                  </button>
                                  <button
                                    onClick={() => handleCopyReturnCase(caseItem)}
                                    disabled={copyReturnMutation.isLoading}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2"
                                  >
                                    <Copy className="h-4 w-4" />
                                    <span>Dönüş Kopyala</span>
                                  </button>
                                  <button
                                    onClick={() => handleDeleteCase(caseItem)}
                                    disabled={deleteMutation.isLoading}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 text-red-600 flex items-center space-x-2 last:rounded-b-lg"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                    <span>Sil</span>
                                  </button>
                                </div>
                              )}
                            </div>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {casesData?.data?.pagination && (
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    {casesData?.data?.pagination?.total_items || 0} vakanın {' '}
                    {((currentPage - 1) * 200) + 1}-{Math.min(currentPage * 200, casesData?.data?.pagination?.total_items || 0)} arası gösteriliyor
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage <= 1}
                      onClick={() => updateSearchParams({ page: currentPage - 1 })}
                    >
                      Önceki
                    </Button>
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const page = i + 1;
                        return (
                          <Button
                            key={page}
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => updateSearchParams({ page })}
                          >
                            {page}
                          </Button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage >= totalPages}
                      onClick={() => updateSearchParams({ page: currentPage + 1 })}
                    >
                      Sonraki
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CasesPage; 